import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { Calendar } from "@/components/ui/calendar";
import {
  CalendarDays, Clock, MapPin, Video, Users,
  ChevronLeft, ChevronRight, TreePine, Phone
} from "lucide-react";

const CATEGORY_STYLES = {
  event: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", label: "Event" },
  appointment: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500", label: "Appointment" },
  group_session: { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500", label: "Group Session" },
  retreat: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", label: "Retreat" },
  community: { bg: "bg-pink-100", text: "text-pink-700", dot: "bg-pink-500", label: "Community" },
  booking: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500", label: "Booking" },
};

const getStyle = (cat) => CATEGORY_STYLES[cat] || CATEGORY_STYLES.event;

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/calendar/events");
      setEvents(res.data || []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const dayEvents = events.filter(e => e.date?.startsWith(selectedDateStr));

  const eventDates = events
    .filter(e => e.date && !isNaN(new Date(e.date).getTime()))
    .map(e => new Date(e.date + "T00:00:00"));

  const upcomingEvents = events
    .filter(e => e.date && !isNaN(new Date(e.date).getTime()) && new Date(e.date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 8);

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case "appointment": case "booking": return Clock;
      case "group_session": return Users;
      case "retreat": return TreePine;
      case "community": return Users;
      default: return CalendarDays;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="calendar-page">
        <div>
          <p className="overline-text mb-2">Your Schedule</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Wellness Calendar
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            All your upcoming events, appointments, sessions, and retreat schedules in one place.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar Widget */}
          <div className="lg:col-span-1 space-y-6">
            <div className="spa-card" data-testid="calendar-widget">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-xl"
                modifiers={{ hasEvent: eventDates }}
                modifiersStyles={{
                  hasEvent: {
                    fontWeight: "bold",
                    textDecoration: "underline",
                    textDecorationColor: "#C5705D",
                    textUnderlineOffset: "4px",
                  },
                }}
              />
            </div>

            {/* Legend */}
            <div className="spa-card" data-testid="calendar-legend">
              <h3 className="font-serif text-sm font-medium text-spa-moss mb-3">Categories</h3>
              <div className="space-y-2">
                {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
                    <span className="text-xs text-spa-text-secondary">{style.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Events Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Selected Day */}
            <div className="spa-card" data-testid="day-events">
              <h2 className="font-serif text-xl text-spa-moss mb-4">
                {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </h2>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" />
                </div>
              ) : dayEvents.length > 0 ? (
                <div className="space-y-3">
                  {dayEvents.map((event, i) => {
                    const style = getStyle(event.category);
                    const Icon = getCategoryIcon(event.category);
                    return (
                      <div key={event.event_id || `evt-${i}`} className="flex items-start gap-4 p-4 rounded-xl bg-spa-muted/50 border border-spa-border/50" data-testid={`event-${event.event_id || i}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                          <Icon className={`w-5 h-5 ${style.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-spa-moss text-sm">{event.title}</h4>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                              {style.label}
                            </span>
                          </div>
                          {event.description && <p className="text-xs text-spa-text-secondary mb-1">{event.description}</p>}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-spa-text-secondary">
                            {event.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {event.time}{event.end_time ? ` - ${event.end_time}` : ""}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {event.location}
                              </span>
                            )}
                            {event.zoom_link && (
                              <a href={event.zoom_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                                <Video className="w-3 h-3" /> Join Zoom
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarDays className="w-10 h-10 text-spa-border mx-auto mb-2" />
                  <p className="text-sm text-spa-text-secondary">No events on this day</p>
                </div>
              )}
            </div>

            {/* Upcoming Events */}
            <div className="spa-card" data-testid="upcoming-events">
              <h2 className="font-serif text-xl text-spa-moss mb-4">Upcoming Events</h2>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map((event, i) => {
                    const style = getStyle(event.category);
                    const Icon = getCategoryIcon(event.category);
                    return (
                      <div
                        key={event.event_id || `up-${i}`}
                        className="flex items-center gap-4 p-3 rounded-xl hover:bg-spa-muted/50 transition-colors cursor-pointer"
                        onClick={() => event.date && setSelectedDate(new Date(event.date + "T00:00:00"))}
                        data-testid={`upcoming-${event.event_id || i}`}
                      >
                        <div className="text-center min-w-[48px]">
                          <p className="text-xs text-spa-terracotta font-medium uppercase">
                            {event.date ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short" }) : ""}
                          </p>
                          <p className="text-xl font-serif text-spa-moss">
                            {event.date ? new Date(event.date + "T00:00:00").getDate() : ""}
                          </p>
                        </div>
                        <div className={`w-1 h-10 rounded-full ${style.dot}`} />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-spa-moss text-sm truncate">{event.title}</h4>
                          <p className="text-xs text-spa-text-secondary">
                            {event.time || ""} {event.location ? `· ${event.location}` : ""}
                          </p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${style.bg} ${style.text} flex-shrink-0`}>
                          {style.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-spa-text-secondary text-center py-4">No upcoming events</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
